<template>
  <div class="profile-page">

    <div class="flash is-ok" v-if="flash">
      <span>{{ flash }}</span>
      <button class="flash-close" @click="flash = ''">&times;</button>
    </div>
    <div class="flash is-err" v-if="errorMessage">
      <span>{{ errorMessage }}</span>
      <button class="flash-close" @click="errorMessage = ''">&times;</button>
    </div>

    <div class="skeleton" v-if="loading">{{ $t('common.loading') }}</div>

    <template v-else-if="profile">

      <!-- Kimlik kartı -->
      <section class="identity">
        <div class="identity-main">
          <div class="avatar">{{ initials }}</div>

          <div class="identity-text">
            <h2 class="identity-name">{{ profile.fullName || profile.email }}</h2>
            <p class="identity-role" v-if="profile.title">{{ profile.title }}</p>
            <p class="identity-mail">
              {{ profile.email }}
              <span class="dot" :class="profile.emailVerifiedAt ? 'is-ok' : 'is-idle'"
                    :title="profile.emailVerifiedAt ? $t('profile.verified') : $t('profile.notVerified')"></span>
            </p>

            <div class="chips">
              <span class="chip is-strong">{{ roleLabel(profile.role) }}</span>
              <span class="chip" :class="profile.status === 'ACTV' ? 'is-ok' : ''">
                {{ statusLabel(profile.status) }}
              </span>
              <span class="chip" v-if="organization">
                <i class="fa fa-building-o"></i> {{ organization.name }}
              </span>
            </div>
          </div>

          <button class="btn-on-dark" v-if="!editing" @click="startEdit">
            <span class="icon"><i class="fa fa-pencil"></i></span>
            <span>{{ $t('common.edit') }}</span>
          </button>
        </div>
      </section>

      <!-- Bilgiler -->
      <section class="card">
        <h3 class="card-title">{{ $t('profile.account') }}</h3>

        <!-- Görüntüleme: tablo yerine ızgara. Tablo dar ekranda yatay
             kayıyordu ve ikinci sütunu okumak zorlaşıyordu. -->
        <dl class="facts" v-if="!editing">
          <div class="fact">
            <dt>{{ $t('profile.fullName') }}</dt>
            <dd>{{ profile.fullName || '—' }}</dd>
          </div>
          <div class="fact">
            <dt>{{ $t('profile.jobTitle') }}</dt>
            <dd>{{ profile.title || '—' }}</dd>
          </div>
          <div class="fact">
            <dt>{{ $t('profile.phone') }}</dt>
            <dd>{{ profile.phone || '—' }}</dd>
          </div>
          <div class="fact">
            <dt>{{ $t('profile.organization') }}</dt>
            <dd>
              <span v-if="organization">
                {{ organization.name }}
                <code>{{ organization.slug }}</code>
              </span>
              <span v-else>—</span>
            </dd>
          </div>
          <div class="fact">
            <dt>{{ $t('profile.locale') }}</dt>
            <dd>{{ localeLabel(profile.locale) }} · {{ profile.timezone }}</dd>
          </div>
          <div class="fact">
            <dt>{{ $t('profile.country') }}</dt>
            <dd>{{ countryLabel(profile.country) }}</dd>
          </div>
          
        </dl>

        <!-- Düzenleme -->
        <div class="form" v-else>
          <div class="field">
            <label>{{ $t('profile.fullName') }}</label>
            <input class="input" v-model.trim="form.fullName" maxlength="150">
          </div>

          <div class="form-row">
            <div class="field">
              <label>{{ $t('profile.jobTitle') }}</label>
              <input class="input" v-model.trim="form.title" maxlength="100"
                     :placeholder="$t('profile.titleHint')">
            </div>
            <div class="field">
              <label>{{ $t('profile.phone') }}</label>
              <input class="input" v-model.trim="form.phone" maxlength="30">
            </div>
          </div>

          <div class="form-row">
            <div class="field">
              <label>{{ $t('organization.inviteLanguage') }}</label>
              <div class="select-wrap">
                <select class="select-input" v-model="form.locale">
                  <option value="tr">Türkçe</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                </select>
              </div>
            </div>
            <div class="field">
              <label>{{ $t('profile.timezone') }}</label>
              <div class="select-wrap">
                <select class="select-input" v-model="form.timezone">
                  <option v-for="zone in timezoneList" :key="zone.code" :value="zone.code">
                    {{ zone.label }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="field">
              <label>{{ $t('profile.country') }}</label>
              <div class="select-wrap">
                <select class="select-input" v-model="form.country">
                  <option value="">—</option>
                  <option v-for="country in countryList" :key="country.code" :value="country.code">
                    {{ country.label }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <p class="hint">{{ $t('profile.lockedHint') }}</p>

          <div class="actions">
            <button class="btn-primary" :class="{ 'is-loading': saving }" @click="save">
              {{ $t('common.save') }}
            </button>
            <button class="btn-ghost" @click="editing = false">{{ $t('common.cancel') }}</button>
          </div>
        </div>
      </section>

      <!-- İmza -->
      <section class="card">
        <h3 class="card-title">{{ $t('profile.signatureTitle') }}</h3>
        <p class="card-lead">{{ $t('profile.signatureLead') }}</p>

        <div v-if="hasSignature && !drawing">
          <div class="sig-paper">
            <img :src="signatureSrc" alt="signature">
            <span class="sig-rule"></span>
          </div>
          <div class="actions center">
            <button class="btn-ghost" @click="startDrawing">{{ $t('profile.change') }}</button>
            <button class="btn-danger" @click="removeSignature">{{ $t('common.delete') }}</button>
          </div>
        </div>

        <div v-else>
          <div class="seg">
            <button :class="{ 'is-active': mode === 'drawn' }" @click="setMode('drawn')">
              {{ $t('profile.tabDraw') }}
            </button>
            <button :class="{ 'is-active': mode === 'typed' }" @click="setMode('typed')">
              {{ $t('profile.tabType') }}
            </button>
          </div>

          <div v-show="mode === 'drawn'" class="pad-wrap">
            <canvas ref="pad" class="pad"
                    @mousedown="startStroke" @mousemove="moveStroke" @mouseup="endStroke"
                    @mouseleave="endStroke"
                    @touchstart.prevent="startStroke" @touchmove.prevent="moveStroke"
                    @touchend.prevent="endStroke"></canvas>
            <span class="sig-rule"></span>
            <p class="hint center">{{ $t('profile.drawHint') }}</p>
          </div>

          <div v-show="mode === 'typed'" class="pad-wrap">
            <input class="input center-text" v-model="typedName"
                   :placeholder="$t('profile.typePlaceholder')" @input="renderTyped">
            <canvas ref="typedPad" class="pad is-typed"></canvas>
            <span class="sig-rule"></span>
          </div>

          <div class="actions center">
            <button class="btn-primary" :class="{ 'is-loading': saving }" @click="saveSignature">
              {{ $t('profile.saveSignature') }}
            </button>
            <button class="btn-ghost" @click="clearSignature">{{ $t('profile.clearSignature') }}</button>
            <button class="btn-ghost" v-if="hasSignature" @click="drawing = false">
              {{ $t('common.cancel') }}
            </button>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>
                  <span class="has-text-grey" v-else>—</span>
                </td>
              </tr>
              <tr>
                <td>{{ $t('profile.role') }}</td>
                <td><span class="tag is-light">{{ roleLabel(profile.role) }}</span></td>
              </tr>
              <tr>
                <td>{{ $t('common.status') }}</td>
                <td>
                  <span class="tag is-small" :class="profile.status === 'ACTV' ? 'is-success is-light' : 'is-light'">
                    {{ statusLabel(profile.status) }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>{{ $t('profile.locale') }}</td>
                <td>{{ localeLabel(profile.locale) }} · {{ profile.timezone }}</td>
              </tr>
            
            </tbody>
          </table>

          <div v-else>
            <div class="field">
              <label class="label is-small">{{ $t('profile.fullName') }}</label>
              <div class="control">
                <input class="input" v-model.trim="form.fullName" maxlength="150">
              </div>
            </div>
            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label is-small">{{ $t('profile.jobTitle') }}</label>
                  <div class="control">
                    <input class="input" v-model.trim="form.title" maxlength="100"
                           :placeholder="$t('profile.titleHint')">
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label is-small">{{ $t('profile.phone') }}</label>
                  <div class="control">
                    <input class="input" v-model.trim="form.phone" maxlength="30">
                  </div>
                </div>
              </div>
            </div>
            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label is-small">{{ $t('organization.inviteLanguage') }}</label>
                  <div class="control">
                    <div class="select is-fullwidth">
                      <select v-model="form.locale">
                        <option value="tr">Türkçe</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label is-small">{{ $t('profile.timezone') }}</label>
                  <div class="control">
                    <input class="input" v-model.trim="form.timezone" placeholder="Europe/Istanbul">
                  </div>
                </div>
              </div>
              <div class="column is-narrow">
                <div class="field">
                  <label class="label is-small">{{ $t('profile.country') }}</label>
                  <div class="control">
                    <input class="input" v-model.trim="form.country" maxlength="2"
                           style="width: 80px; text-transform: uppercase;" placeholder="TR">
                  </div>
                </div>
              </div>
            </div>

            <p class="help mb-3">
              {{ $t('profile.lockedHint') }}
            </p>

            <div class="buttons">
              <button class="button is-primary is-small" :class="{ 'is-loading': saving }"
                      @click="save">{{ $t('common.save') }}</button>
              <button class="button is-small" @click="editing = false">{{ $t('common.cancel') }}</button>
            </div>
          </div>
        </div>

        <!-- Varsayılan imza -->
        <div class="box">
          <h2 class="title is-6">{{ $t('profile.signatureTitle') }}</h2>
          <p class="has-text-grey is-size-7 mb-4">
            {{ $t('profile.signatureLead') }}
          </p>

          <!-- Kayıtlı imza -->
          <div v-if="hasSignature && !drawing">
            <div class="signature-preview">
              <img :src="signatureSrc" alt="signature">
            </div>
            <div class="buttons mt-3">
              <button class="button is-small" @click="startDrawing">{{ $t('profile.change') }}</button>
              <button class="button is-small is-danger is-light" @click="removeSignature">{{ $t('common.delete') }}</button>
            </div>
          </div>

          <!-- Çizim -->
          <div v-else>
            <div class="tabs is-toggle is-small">
              <ul>
                <li :class="{ 'is-active': mode === 'drawn' }"><a @click="setMode('drawn')">{{ $t('profile.tabDraw') }}</a></li>
                <li :class="{ 'is-active': mode === 'typed' }"><a @click="setMode('typed')">{{ $t('profile.tabType') }}</a></li>
              </ul>
            </div>

            <div v-show="mode === 'drawn'">
              <canvas ref="pad"
                      style="width:100%;height:170px;border:1px dashed #b5b5b5;border-radius:4px;touch-action:none;background:#fff;"
                      @mousedown="startStroke" @mousemove="moveStroke" @mouseup="endStroke" @mouseleave="endStroke"
                      @touchstart.prevent="startStroke" @touchmove.prevent="moveStroke" @touchend.prevent="endStroke">
              </canvas>
              <p class="help">{{ $t('profile.drawHint') }}</p>
            </div>

            <div v-show="mode === 'typed'">
              <input class="input" v-model="typedName" :placeholder="$t('profile.typePlaceholder')" @input="renderTyped">
              <canvas ref="typedPad"
                      style="width:100%;height:120px;border:1px solid #ededed;border-radius:4px;background:#fff;margin-top:8px;">
              </canvas>
            </div>

            <div class="buttons mt-3">
              <button class="button is-primary is-small" :class="{ 'is-loading': saving }"
                      @click="saveSignature">{{ $t('profile.saveSignature') }}</button>
              <button class="button is-small" @click="clearSignature">{{ $t('profile.clearSignature') }}</button>
              <button class="button is-small" v-if="hasSignature" @click="drawing = false">{{ $t('common.cancel') }}</button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import api from '../config/api';
import geo from '../config/geo';

export default {
  name: 'Profile',
  data() {
    return {
      profile: null,
      organization: null,

      loading: true,
      saving: false,
      editing: false,
      flash: '',
      errorMessage: '',
      form: {},

      // İmza
      drawing: false,
      hasSignature: false,
      signatureSrc: '',
      mode: 'drawn',
      typedName: '',
      isDrawing: false,
      hasStroke: false,
      ctx: null
    };
  },
  computed: {
    // Listeler dile bağlı: dil değişince ülke adları da değişiyor.
    // Kullanıcının kayıtlı değeri listede yoksa başa ekleniyor, yoksa
    // kaydet dediğinde farkında olmadan değiştirmiş oluyor.
    countryList() {
      return geo.countries(this.$locale, this.form.country || (this.profile && this.profile.country));
    },
    timezoneList() {
      return geo.timezones(this.$locale, this.form.timezone || (this.profile && this.profile.timezone));
    },

    // Baş harfler: ayrı bir avatar paketine bağlanmadan kimlik hissi verir
    initials() {
      if (!this.profile) return '?';
      let source = this.profile.fullName || this.profile.email || '';

      // E-postada alan adı kimliğin parçası değil: ayse.yilmaz@ornek.com
      // "AO" değil "AY" vermeli
      const at = source.indexOf('@');
      if (!this.profile.fullName && at > 0) source = source.slice(0, at);

      const parts = source.trim().split(/[\s._-]+/).filter(Boolean);
      if (!parts.length) return '?';

      // Büyük harf dile bağlı: Türkçe'de i → İ, İngilizce'de i → I
      const locale = this.$locale === 'tr' ? 'tr-TR' : 'en-US';
      const take = parts.length === 1
        ? parts[0].slice(0, 2)
        : parts[0][0] + parts[parts.length - 1][0];
      return take.toLocaleUpperCase(locale);
    }
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    // Blob URL bırakılmazsa sekme kapanana kadar bellekte kalır
    if (this.signatureSrc) window.URL.revokeObjectURL(this.signatureSrc);
  },
  methods: {
    load() {
      api.get('/profile')
        .then((body) => {
          this.profile = body.profile;
          this.organization = body.organization;
          this.typedName = body.profile.fullName || '';
          this.hasSignature = !!(body.profile.signature && body.profile.signature.updatedAt);
          if (this.hasSignature) this.loadSignature();
          else this.$nextTick(() => this.setupCanvas());
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.loading = false; });
    },

    loadSignature() {
      api.get('/profile/signature', { responseType: 'blob' })
        .then((blob) => {
          if (blob.type && blob.type.indexOf('application/json') > -1) {
            this.hasSignature = false;
            return;
          }
          this.signatureSrc = window.URL.createObjectURL(blob);
        })
        .catch(() => { this.hasSignature = false; });
    },

    startEdit() {
      this.form = {
        fullName: this.profile.fullName,
        title: this.profile.title,
        phone: this.profile.phone,
        locale: this.profile.locale,
        // Kayıtlı değer yoksa tarayıcının saat dilimi makul bir başlangıç
        timezone: this.profile.timezone || geo.guessTimezone(),
        country: this.profile.country || ''
      };
      this.editing = true;
    },

    save() {
      if (this.saving) return;
      if (!this.form.fullName || this.form.fullName.length < 2) {
        this.errorMessage = this.$t('profile.errName');
        return;
      }
      this.saving = true;
      api.put('/profile', this.form)
        .then((body) => {
          this.profile = body.profile;
          this.editing = false;
          this.notify(body.message);
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    // --- İmza tuvali ---------------------------------------------------

    startDrawing() {
      this.drawing = true;
      this.hasStroke = false;
      this.$nextTick(() => {
        this.setupCanvas();
        this.renderTyped();
      });
    },

    setupCanvas() {
      const canvas = this.$refs.pad;
      if (!canvas) return;
      // Ekran yoğunluğuna göre ölçekle, yoksa çizgi bulanık çıkıyor
      const ratio = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      this.ctx = canvas.getContext('2d');
      this.ctx.scale(ratio, ratio);
      this.ctx.lineWidth = 2.2;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.strokeStyle = '#1a1a4b';
    },

    pointerPos(event) {
      const rect = this.$refs.pad.getBoundingClientRect();
      const source = event.touches && event.touches.length ? event.touches[0] : event;
      return { x: source.clientX - rect.left, y: source.clientY - rect.top };
    },

    startStroke(event) {
      if (!this.ctx) this.setupCanvas();
      this.isDrawing = true;
      const p = this.pointerPos(event);
      this.ctx.beginPath();
      this.ctx.moveTo(p.x, p.y);
    },
    moveStroke(event) {
      if (!this.isDrawing) return;
      const p = this.pointerPos(event);
      this.ctx.lineTo(p.x, p.y);
      this.ctx.stroke();
      this.hasStroke = true;
    },
    endStroke() {
      this.isDrawing = false;
    },

    clearSignature() {
      if (this.ctx) {
        const canvas = this.$refs.pad;
        this.ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      this.hasStroke = false;
    },

    setMode(mode) {
      this.mode = mode;
      this.$nextTick(() => {
        if (mode === 'drawn') this.setupCanvas();
        else this.renderTyped();
      });
    },

    renderTyped() {
      const canvas = this.$refs.typedPad;
      if (!canvas) return;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#1a1a4b';
      ctx.font = 'italic 42px "Brush Script MT", "Segoe Script", cursive';
      ctx.textBaseline = 'middle';
      ctx.fillText(this.typedName || '', 20, canvas.offsetHeight / 2);
    },

    saveSignature() {
      if (this.saving) return;
      const filled = this.mode === 'drawn' ? this.hasStroke : !!String(this.typedName || '').trim();
      if (!filled) {
        this.errorMessage = this.$t('profile.errSignature');
        return;
      }

      const canvas = this.mode === 'drawn' ? this.$refs.pad : this.$refs.typedPad;
      this.saving = true;
      api.put('/profile/signature', {
        image: canvas.toDataURL('image/png'),
        type: this.mode
      })
        .then((body) => {
          this.notify(body.message);
          this.drawing = false;
          this.hasSignature = true;
          if (this.signatureSrc) window.URL.revokeObjectURL(this.signatureSrc);
          this.signatureSrc = '';
          this.loadSignature();
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    removeSignature() {
      api.delete('/profile/signature')
        .then((body) => {
          this.notify(body.message);
          this.hasSignature = false;
          if (this.signatureSrc) window.URL.revokeObjectURL(this.signatureSrc);
          this.signatureSrc = '';
          this.$nextTick(() => this.setupCanvas());
        })
        .catch((err) => { this.errorMessage = err.message; });
    },

    // --- Etiketler ------------------------------------------------------

    notify(message) {
      this.flash = message;
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { this.flash = ''; }, 3000);
    },
    roleLabel(role) {
      const map = { '5': 'roleOwner', '4': 'roleAdmin', '1': 'roleMember', '0': 'roleViewer' };
      return map[role] ? this.$t('profile.' + map[role]) : role;
    },
    statusLabel(status) {
      const map = { ACTV: 'stActive', INVT: 'stInvited', PASV: 'stPassive', SUSP: 'stSuspended' };
      return map[status] ? this.$t('profile.' + map[status]) : status;
    },
    countryLabel(code) {
      if (!code) return '—';
      const found = geo.countries(this.$locale, code).find((c) => c.code === code);
      return found ? found.label : code;
    },
    localeLabel(locale) {
      return { tr: 'Türkçe', en: 'English', fr: 'Français' }[locale] || locale;
    },
    formatDate(value) {
      return this.$formatDate(value);
    }
  }
};
</script>

<style scoped>
/* Ortalanmış, dar bir kolon: profil bir tablo değil, bir kimlik sayfası.
   Geniş ekranda yayılan formlar okunaklı olmuyor. */
.profile-page {
  max-width: 680px;
  margin: 0 auto;
}

/* --- Bildirim --- */
.flash {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  margin-bottom: 16px;
}

.flash.is-ok { background: #e9f7f2; color: #0f6e5c; }
.flash.is-err { background: #fdeceb; color: #a8322c; }

.flash-close {
  background: transparent;
  border: 0;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
}

.skeleton {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  color: #98a0b5;
}

/* --- Kart --- */
.card {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 18px;
}

.card-title {
  position: relative;
  font-size: 15px;
  font-weight: 700;
  color: #1e2233;
  margin-bottom: 4px;
  padding-left: 12px;
}

.card-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 3px;
  bottom: 3px;
  width: 3px;
  border-radius: 2px;
  background: #00d1b2;
}

.card-lead {
  font-size: 13px;
  color: #79809a;
  margin-bottom: 18px;
  line-height: 1.6;
}

/* --- Kimlik --- */
.identity {
  position: relative;
  overflow: hidden;
  border-radius: 14px;
  margin-bottom: 18px;
  padding: 26px 24px;
  background: linear-gradient(125deg, #1d1d38 0%, #2f2f5a 55%, #3a3d75 100%);
  box-shadow: 0 10px 28px rgba(24, 24, 48, 0.22);
}

/* Köşedeki yumuşak ışık: düz degrade biraz cansız duruyordu */
.identity::after {
  content: "";
  position: absolute;
  right: -70px;
  top: -90px;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 209, 178, 0.28) 0%, rgba(0, 209, 178, 0) 70%);
  pointer-events: none;
}

.identity-main {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 18px;
}

.avatar {
  flex: 0 0 66px;
  width: 66px;
  height: 66px;
  border-radius: 18px;
  background: linear-gradient(140deg, #00d1b2 0%, #00a88f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 23px;
  font-weight: 700;
  color: #0b2a26;
  letter-spacing: 0.5px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.22);
}

.identity-text {
  flex: 1 1 auto;
  min-width: 0;
  padding-bottom: 2px;
}

.identity-name {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  line-height: 1.25;
}

.identity-role {
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.72);
  margin-top: 3px;
}

.identity-mail {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.58);
  margin-top: 6px;
  word-break: break-all;
}

/* Doğrulama durumu için ayrı bir etiket yerine küçük bir nokta:
   bilgi ikincil, satırı şişirmesine gerek yok */
.dot {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  /* gap desteklemeyen eski motorlarda nokta yazıya yapışıyordu */
  margin-left: 2px;
}

.dot.is-ok { background: #21e0a8; box-shadow: 0 0 0 3px rgba(33, 224, 168, 0.22); }
.dot.is-idle { background: rgba(255, 255, 255, 0.35); }

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.13);
  color: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  padding: 4px 11px;
  font-size: 12px;
  font-weight: 600;
}

.chip.is-strong {
  background: rgba(255, 255, 255, 0.95);
  color: #22223d;
  border-color: transparent;
}

.chip.is-ok {
  background: rgba(0, 209, 178, 0.2);
  color: #7cf2dc;
  border-color: rgba(0, 209, 178, 0.35);
}

.chip i { opacity: 0.75; }

/* --- Bilgi ızgarası --- */
.facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  background: #eef0f6;
  border: 1px solid #eef0f6;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 16px;
}

.fact {
  background: #fff;
  padding: 13px 15px;
}

.fact dt {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa1b5;
  margin-bottom: 4px;
}

.fact dd {
  font-size: 14px;
  color: #2b3146;
  word-break: break-word;
}

.fact dd.is-muted { color: #79809a; font-size: 13px; }

.fact code {
  background: #f2f4fa;
  color: #5a6180;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 12px;
}

/* --- Form --- */
.form { margin-top: 14px; }

.field { margin-bottom: 14px; flex: 1 1 0; min-width: 0; }
.field.is-narrow { flex: 0 0 90px; }

.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #5a6180;
  margin-bottom: 5px;
}

.form-row {
  display: flex;
  gap: 12px;
}

.input,
.select-input {
  width: 100%;
  height: 40px;
  border: 1px solid #dde1ea;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #2b3146;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.input:focus,
.select-input:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.16);
}

/* Bulma'nın .select sarmalayıcısı sabit yükseklik ve kendi okunu
   dayatıyordu; içerideki seçenek metni kırpılıyordu. Kendi okumuzu
   çiziyor, tarayıcının varsayılanını kapatıyoruz. */
.select-wrap {
  position: relative;
  display: block;
}

.select-input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 34px;
  cursor: pointer;
  line-height: normal;
}

.select-wrap::after {
  content: "";
  position: absolute;
  right: 14px;
  top: 50%;
  margin-top: -2px;
  width: 0;
  height: 0;
  border-left: 4.5px solid transparent;
  border-right: 4.5px solid transparent;
  border-top: 5px solid #8b93a8;
  pointer-events: none;
}

.input.is-code { text-transform: uppercase; letter-spacing: 0.08em; }
.input.center-text { text-align: center; }

.hint {
  font-size: 12.5px;
  color: #98a0b5;
  margin: 4px 0 16px;
}

.hint.center { text-align: center; margin-top: 10px; }

/* --- Düğmeler --- */
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.actions.center { justify-content: center; margin-top: 16px; }

.btn-primary,
.btn-ghost,
.btn-danger {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.btn-primary {
  background: linear-gradient(135deg, #00d1b2 0%, #00a88f 100%);
  border: 1px solid transparent;
  color: #06302a;
  box-shadow: 0 3px 10px rgba(0, 168, 143, 0.28);
}

.btn-primary:hover {
  background: linear-gradient(135deg, #0ddcbe 0%, #00b79c 100%);
  box-shadow: 0 5px 14px rgba(0, 168, 143, 0.34);
}

.btn-ghost {
  background: #fff;
  border: 1px solid #dde1ea;
  color: #3c4257;
}

.btn-ghost:hover {
  border-color: #00a88f;
  color: #0b6e5f;
}

.btn-danger { background: #fff; border: 1px solid #f0cdcb; color: #b63934; }
.btn-danger:hover { background: #fdf3f2; border-color: #e0a9a5; }

/* Koyu banner üzerindeki düğme: çerçeve beyaz, zemin şeffaf */
.btn-on-dark {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 8px;
  padding: 9px 15px;
  font-size: 13.5px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.btn-on-dark:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.45);
}

.btn-primary.is-loading { opacity: 0.7; pointer-events: none; }

/* --- İmza --- */
/* Kağıt hissi: imza bir form alanı gibi değil, bir belgeye atılmış gibi
   görünsün. Altındaki ince çizgi imza satırını taklit ediyor. */
.sig-paper {
  position: relative;
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 10px;
  padding: 26px 26px 34px;
  text-align: center;
  box-shadow: inset 0 1px 0 #fafbfd;
}

.sig-paper img {
  max-height: 110px;
  max-width: 100%;
}

.sig-rule {
  display: block;
  height: 1px;
  background: #dfe3ec;
  margin: 10px 32px 0;
}

.seg {
  display: inline-flex;
  background: #f2f4fa;
  border-radius: 999px;
  padding: 3px;
  margin-bottom: 14px;
}

.seg button {
  background: transparent;
  border: 0;
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #79809a;
  cursor: pointer;
}

.seg button.is-active {
  background: #fff;
  color: #0b6e5f;
  box-shadow: 0 1px 3px rgba(24, 24, 48, 0.12);
}

.pad-wrap { margin-top: 4px; }

.pad {
  display: block;
  width: 100%;
  height: 170px;
  background: #fff;
  border: 1px dashed #cfd5e2;
  border-radius: 10px;
  cursor: crosshair;
  touch-action: none;
}

.pad.is-typed {
  height: 120px;
  border-style: solid;
  border-color: #eef0f6;
  cursor: default;
  margin-top: 10px;
}

@media screen and (max-width: 640px) {
  .facts { grid-template-columns: 1fr; }
  .form-row { flex-direction: column; gap: 0; }
  .field.is-narrow { flex: 1 1 auto; }
  .identity-main { flex-direction: column; align-items: flex-start; gap: 14px; }
  .identity { padding: 22px 18px; }
}
</style>


// WEBPACK FOOTER //
// src/Components/Profile.vue